import { IEndereco } from "../../../core/interfaces/IEndereco";
import { TipoPedido } from "../../enum/TipoPedido";
import { ICupom } from "../../interface/ICupom";
import { IPagamentoPedido } from "../../interface/IPagamentoPedido";
import { IProdutoPedido } from "../../interface/IProdutoPedido";

export interface IChatPdVPersistedState {
    savedAt: number;
    tipo: TipoPedido;
    produtos: IProdutoPedido[];
    pagamentos: IPagamentoPedido[];
    endereco: IEndereco | undefined;
    agendadoPara: string | null;
    cupons: ICupom[];
    switchNotaFiscal: boolean;
    switchImprimirAutomatico: boolean;
    /**
     * Vínculo do cliente com três estados:
     * - `string`: cliente vinculado;
     * - `null`: o operador desvinculou de propósito;
     * - ausente/`undefined`: ainda não se sabe — quem manda é o servidor.
     *
     * A distinção existe porque o snapshot é salvo com debounce e pode ser
     * gravado antes de o cliente do chat terminar de carregar; sem ela, um
     * snapshot prematuro apagava o vínculo que o servidor já tinha.
     */
    clienteUuid?: string | null;
}

interface IRascunhoPersistido {
    value: string;
    savedAt: number;
}

const STORAGE_PREFIX = 'chatpdv_';
const RASCUNHO_PREFIX = 'chatpdv_rascunho_';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export const saveState = (chatUuid: string, state: Omit<IChatPdVPersistedState, 'savedAt'>): void => {
    try {
        const entry: IChatPdVPersistedState = { ...state, savedAt: Date.now() };
        localStorage.setItem(`${STORAGE_PREFIX}${chatUuid}`, JSON.stringify(entry));
    } catch {
        // localStorage pode estar cheio ou indisponível
    }
};

export const loadState = (chatUuid: string): IChatPdVPersistedState | null => {
    try {
        const raw = localStorage.getItem(`${STORAGE_PREFIX}${chatUuid}`);
        if (!raw) return null;

        const entry: IChatPdVPersistedState = JSON.parse(raw);
        if (Date.now() - entry.savedAt > MAX_AGE_MS) {
            localStorage.removeItem(`${STORAGE_PREFIX}${chatUuid}`);
            return null;
        }

        return entry;
    } catch {
        return null;
    }
};

export const clearState = (chatUuid: string): void => {
    try {
        localStorage.removeItem(`${STORAGE_PREFIX}${chatUuid}`);
        localStorage.removeItem(`${RASCUNHO_PREFIX}${chatUuid}`);
    } catch {
        // ignore
    }
};

export const saveRascunho = (chatUuid: string, texto: string): void => {
    try {
        if (!texto) {
            localStorage.removeItem(`${RASCUNHO_PREFIX}${chatUuid}`);
            return;
        }
        const entry: IRascunhoPersistido = { value: texto, savedAt: Date.now() };
        localStorage.setItem(`${RASCUNHO_PREFIX}${chatUuid}`, JSON.stringify(entry));
    } catch {
        // ignore
    }
};

export const loadRascunho = (chatUuid: string): string => {
    try {
        const raw = localStorage.getItem(`${RASCUNHO_PREFIX}${chatUuid}`);
        if (!raw) return '';
        const entry = JSON.parse(raw) as IRascunhoPersistido;
        if (Date.now() - entry.savedAt > MAX_AGE_MS) {
            localStorage.removeItem(`${RASCUNHO_PREFIX}${chatUuid}`);
            return '';
        }
        return entry.value ?? '';
    } catch {
        return '';
    }
};

export const cleanExpiredStates = (): void => {
    try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (!key) continue;

            // Rascunho: prefixo mais específico — checar primeiro
            if (key.startsWith(RASCUNHO_PREFIX)) {
                const raw = localStorage.getItem(key);
                if (!raw) { keysToRemove.push(key); continue; }
                try {
                    const entry = JSON.parse(raw) as IRascunhoPersistido;
                    if (Date.now() - entry.savedAt > MAX_AGE_MS) keysToRemove.push(key);
                } catch {
                    keysToRemove.push(key);
                }
                continue;
            }

            if (!key.startsWith(STORAGE_PREFIX)) continue;

            const raw = localStorage.getItem(key);
            if (!raw) {
                keysToRemove.push(key);
                continue;
            }

            try {
                const entry: IChatPdVPersistedState = JSON.parse(raw);
                if (Date.now() - entry.savedAt > MAX_AGE_MS) {
                    keysToRemove.push(key);
                }
            } catch {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch {
        // ignore
    }
};
